import React, { useEffect, useState } from "react";
import { Loader2, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { todayInput } from "@/lib/date";

const OTHER = "other";
const EMPTY = { activity_date: "", activity_id: "", custom_name: "", custom_description: "", points: "", notes: "" };

export default function ThespianClaimDialog({ open, setOpen, activities, editing, onDone }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (!open) return;
    setForm(editing ? {
      activity_date: editing.activity_date, activity_id: editing.activity_id, points: String(editing.points_claimed), notes: editing.notes,
      custom_name: editing.is_other ? editing.activity_name : "", custom_description: editing.activity_description || "",
    } : EMPTY);
  }, [open, editing]);

  const isOther = form.activity_id === OTHER;
  const act = activities.find((a) => a.id === form.activity_id);
  const missingAct = editing && !isOther && !act && form.activity_id === editing.activity_id;

  const submit = async () => {
    if (!form.activity_date || !form.activity_id || !form.points || !form.notes.trim()) { toast.error("All fields are required"); return; }
    if (isOther && (!form.custom_name.trim() || !form.custom_description.trim())) { toast.error("Enter a name and description for your Other activity"); return; }
    setSaving(true);
    const body = { ...form, points: Number(form.points) };
    try {
      if (editing) await api.put(`/thespian/claims/${editing.id}`, body);
      else await api.post("/thespian/claims", body);
      toast.success(editing ? (editing.status === "returned" ? "Claim resubmitted" : "Claim updated") : "Claim submitted for review");
      setOpen(false); onDone();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-h-[90dvh] max-w-lg overflow-y-auto" data-testid="thespian-claim-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{editing ? (editing.status === "returned" ? "Fix & resubmit claim" : "Edit claim") : "New Thespian point claim"}</DialogTitle>
          <DialogDescription>Your director reviews every claim and may adjust the points before approving.</DialogDescription>
        </DialogHeader>
        {editing?.return_note && editing.status === "returned" && (
          <p className="rounded-lg bg-violet-50 px-3 py-2 text-sm text-violet-800" data-testid="thespian-claim-return-note">Director: {editing.return_note}</p>
        )}
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Activity</Label>
            <Select value={form.activity_id} onValueChange={set("activity_id")}>
              <SelectTrigger data-testid="thespian-activity-select"><SelectValue placeholder={missingAct ? editing.activity_name : "Select an ITS activity"} /></SelectTrigger>
              <SelectContent className="max-h-72 max-w-[min(32rem,90vw)]">
                {activities.map((a) => <SelectItem key={a.id} value={a.id} data-testid={`thespian-activity-option-${a.id}`}>{a.name}</SelectItem>)}
                <SelectItem value={OTHER} className="border-t border-slate-100 font-semibold" data-testid="thespian-activity-option-other">Other (not listed)</SelectItem>
              </SelectContent>
            </Select>
            {(act?.guideline || (missingAct && editing.activity_guideline)) && (
              <p className="flex items-start gap-1.5 rounded-md bg-amber-50 px-2.5 py-1.5 text-xs font-medium text-amber-800" data-testid="thespian-activity-guideline">
                <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" /> {act?.guideline || editing.activity_guideline}
              </p>
            )}
          </div>
          {isOther && (
            <div className="space-y-3 rounded-lg border border-amber-200 bg-amber-50/40 p-3" data-testid="thespian-other-fields">
              <div className="space-y-2"><Label>Activity name</Label><Input value={form.custom_name} onChange={(e) => set("custom_name")(e.target.value)} placeholder="e.g. Assistant directed a middle school play" data-testid="thespian-custom-name-input" /></div>
              <div className="space-y-2"><Label>Activity description</Label><Textarea rows={2} value={form.custom_description} onChange={(e) => set("custom_description")(e.target.value)} placeholder="What you did and why it should count toward ITS points" data-testid="thespian-custom-description-input" /></div>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Activity date</Label>
              <Input type="date" max={todayInput()} value={form.activity_date} onChange={(e) => set("activity_date")(e.target.value)} data-testid="thespian-date-input" />
            </div>
            <div className="space-y-2">
              <Label>Points claimed</Label>
              <Input type="number" min="0.5" max="100" step="0.5" value={form.points} onChange={(e) => set("points")(e.target.value)} placeholder="e.g. 1.5" data-testid="thespian-points-input" />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Documentation notes</Label>
            <Textarea rows={4} value={form.notes} onChange={(e) => set("notes")(e.target.value)} placeholder="Hours logged, dates, show title, role, who can verify…" data-testid="thespian-notes-input" />
          </div>
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={saving} className="rounded-full bg-amber-700 hover:bg-amber-800" data-testid="thespian-claim-submit">
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{editing ? (editing.status === "returned" ? "Resubmit" : "Save") : "Submit claim"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
